import {
  Card,
  CardBody,
  Input,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  Button,
} from '@windmill/react-ui';
import InputSearchDate from 'components/form/InputSearchDate';
import LabelArea from 'components/form/LabelArea';
import CareerTable from 'components/career/CareerTable';
import DataWrapper from './DataWrapper';
import NotFound from 'components/table/NotFound';
import PageTitle from 'components/Typography/PageTitle';
import useFilter from 'hooks/useFilter';
import { useState, useEffect, useContext } from 'react';
import CareerServices from 'services/CareerServices';
import sidebar from 'routes/sidebar';
import { FiRefreshCcw } from 'react-icons/fi';
import { SidebarContext } from 'context/SidebarContext';
import { Tabs, Tab } from '@mui/material';

const Career = () => {
  const adminInfo = localStorage.getItem('adminInfo')
    ? JSON.parse(localStorage.getItem('adminInfo'))
    : null;

  const { isUpdate, setIsUpdate } = useContext(SidebarContext);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [status, setStatus] = useState('');
  const [tab, setTab] = useState(0); 
  const [careerData, setCareerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { careerRef, dataTable, serviceData, handleSubmitCareer, currentPage } =
    useFilter(careerData, itemsPerPage);
        useEffect(() => {
    if (dataTable.length !== 0) {
      setItemsPerPage(dataTable.length);
    }
  }, [dataTable,serviceData]);

  const [searchstartDate, setSearchStartDate] = useState(null);
  const [searchendDate, setSearchEndDate] = useState(null);

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // fetch careers depending on active tab
  const fetchCareers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare request parameters
      const params = {
        from: searchstartDate ? formatDate(searchstartDate) : null,
        to: searchendDate ? formatDate(searchendDate) : null,
      };

      // Add status based on tab selection
      if (tab === 1) {
        params.status = 1; // Shortlisted
      } else if (tab === 2) {
        params.status = 2; // Rejected
      }
      // For tab 0 (All), we don't add a status filter

      const response = await CareerServices.getAllCareer(params);
      
      // Handle response based on your API structure
      if (Array.isArray(response)) {
        // If the response is directly an array
        const updatedData = response.map(
          ({ id, name, job_roles, resume, total_experience, summary, ...rest }) => ({
            id,
            name,
            job_roles,
            resume,
            total_experience,
            summary,
            ...rest,
          })
        );
        setCareerData(updatedData);
        setStatus(true);
      } else if (response && response.data) {
        // If the response has a data property
        const updatedData = response.data.map(
          ({ id, name, job_roles, resume, total_experience, summary, ...rest }) => ({
            id,
            name,
            job_roles,
            resume,
            total_experience,
            summary,
            ...rest,
          })
        );
        setCareerData(updatedData);
        setStatus(response.status);
      } else {
        setCareerData([]);
        setStatus(false);
      }
    } catch (err) {
      console.error('Error fetching careers:', err);
      setError('Failed to fetch career data');
      setCareerData([]);
      setStatus(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareers();
    setIsUpdate(false);
  }, [tab, isUpdate]);

  useEffect(() => {
    if (serviceData.length !== 0) {
      setItemsPerPage(serviceData.length);
    }
  }, [serviceData]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSearch = () => {
    setIsUpdate(true);
  };

  const handleRefresh = () => {
    setSearchEndDate(null);
    setSearchStartDate(null);
    setIsUpdate(true);
  };

  // permissions
  const isAdmin = adminInfo?.id === 1;
  const hideView = isAdmin
    ? true
    : adminInfo?.permission?.some(
        (perm) => perm.id_tab === 'career' && perm.view === 1
      );

  return (
    <>
      {hideView && (
        <>
          <PageTitle sidebar={sidebar().sidebar} />

          {/* Search & Filters */}
          <Card className="min-w-0 shadow-xs overflow-hidden bg-white dark:bg-gray-800 mb-5">
            <CardBody>
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1">
                  <Input
                    ref={careerRef}
                    className="border h-10 text-sm focus:outline-none block w-full bg-gray-100 border-transparent focus:bg-white dark:bg-gray-700 dark:focus:bg-gray-600"
                    type="search"
                    name="search"
                    autoComplete="off"
                    placeholder="Search by name, job role, or contact..."
                    onChange={handleSubmitCareer}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-2">
                    <LabelArea label="Start Date" />
                    <div className="w-40">
                      <InputSearchDate
                        value={searchstartDate}
                        onChange={setSearchStartDate}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <LabelArea label="End Date" />
                    <div className="w-40">
                      <InputSearchDate
                        value={searchendDate}
                        minDate={searchstartDate}
                        onChange={setSearchEndDate}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    className="rounded-md h-10 px-4"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    Search
                  </Button>

                  <Button
                    layout="outline"
                    className="rounded-md h-10 px-3"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <FiRefreshCcw className="mr-1" />
                    Reset
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { backgroundColor: '#057a55' } }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 'medium',
                color: 'gray',
                '&.Mui-selected': { color: '#057a55', fontWeight: 'semibold' }
              },
              marginBottom: '16px'
            }}
          >
            <Tab label="All Applications" />
            <Tab label="Shortlisted" />
            <Tab label="Rejected" />
          </Tabs>

          {/* Data */}
          <DataWrapper loading={loading} error={error} status={status}>
            {serviceData?.length !== 0 ? (
              <TableContainer className="mb-4">
                <div
                  className="overflow-y-auto"
                  style={{ maxHeight: '60vh' }}
                >
                  <Table>
               <TableHeader className="bg-gray-100 dark:bg-gray-700 sticky top-0">
  <tr>
    <TableCell className="font-semibold text-center">Sl.No.</TableCell>
    <TableCell className="font-semibold text-center">Name</TableCell>
    <TableCell className="font-semibold text-center">Job Role</TableCell>
    <TableCell className="font-semibold text-center">Date</TableCell>
    <TableCell className="font-semibold text-center">Contact</TableCell>
    <TableCell className="font-semibold text-center">Resume</TableCell>
    <TableCell className="font-semibold text-center">Status</TableCell>
<TableCell className="font-semibold text-center">View</TableCell>
    {/* Show Reason column based on tab */}
    {(tab === 0 || tab === 2) && (
      <TableCell className="font-semibold text-center">Reason</TableCell>
    )}

    
  </tr>
</TableHeader>


                    <CareerTable
                      career={dataTable}
                      perpage={itemsPerPage}
                      currentPage={currentPage}
                      tab={tab} 
					  fetchCareers={fetchCareers}  // Pass the tab prop to CareerTable
                    />
                  </Table>
                </div>
              </TableContainer>
            ) : (
              <NotFound title="There are no career applications." />
            )}
          </DataWrapper>
        </>
      )}
    </>
  );
};

export default Career; 